import {IPointData} from 'pixi.js';

type DistanceType = 'manhattan' | 'euclidean' | 'chebyshev';
type Range = [number, number];
type Axis = 'horizontal' | 'vertical';
type Inclusivity = '[]' | '[)' | '(]' | '()';
type RangeOptions = {
    range: Range;
    inclusivity?: Inclusivity;
};

export interface IGridPosition {
    reel: number;
    row: number;
}

/**
 * Represents a position on a 2D grid (reel, row).
 * Provides helpers for comparing, moving and measuring distance between positions.
 */
export class GridPosition implements IGridPosition {
    constructor(
        public reel: number,
        public row: number
    ) {}

    /**
     * Creates a GridPosition from an object with `{reel, row}`.
     * Useful when you have plain grid coordinates and want a GridPosition
     * to access its helper methods.
     */
    public static fromPosition(p: IGridPosition): GridPosition {
        return new GridPosition(p.reel, p.row);
    }

    /**
     * Create a GridPosition from a point object `{x, y}`.
     * Maps `x → reel` and `y → row`.
     */
    public static fromPoint(p: IPointData): GridPosition {
        return new GridPosition(p.x, p.y);
    }

    /**
     * Create a GridPosition from a tuple `[reel, row]`.
     */
    public static fromTuple([reel, row]: [number, number]): GridPosition {
        return new GridPosition(reel, row);
    }

    /** Returns true if this position equals the other. */
    public equals(other: IGridPosition): boolean {
        return this.isSameReel(other) && this.isSameRow(other);
    }

    /** Returns true if both positions are in the same column. */
    public isSameReel(other: IGridPosition): boolean {
        return this.reel === other.reel;
    }

    /** Returns true if both positions are in the same row. */
    public isSameRow(other: IGridPosition): boolean {
        return this.row === other.row;
    }

    /** Returns true if this position is directly above the other (same column). */
    public isDirectlyAbove(other: IGridPosition): boolean {
        return this.isAbove(other) && this.isSameReel(other);
    }

    /** Returns true if this position is directly below the other (same column). */
    public isDirectlyBelow(other: IGridPosition): boolean {
        return this.isBelow(other) && this.isSameReel(other);
    }

    /** Returns true if this position is somewhere above the other (ignores column). */
    public isAbove(other: IGridPosition): boolean {
        return this.row < other.row;
    }

    /** Returns true if this position is somewhere below the other (ignores column). */
    public isBelow(other: IGridPosition): boolean {
        return this.row > other.row;
    }

    /** Returns true if this position is directly to the left of the other (same row). */
    public isDirectlyLeftOf(other: IGridPosition): boolean {
        return this.isLeftOf(other) && this.isSameRow(other);
    }

    /** Returns true if this position is directly to the right of the other (same row). */
    public isDirectlyRightOf(other: IGridPosition): boolean {
        return this.isRightOf(other) && this.isSameRow(other);
    }

    /** Returns true if this position lies somewhere to the left of the other (ignores row). */
    public isLeftOf(other: IGridPosition): boolean {
        return this.reel < other.reel;
    }

    /** Returns true if this position lies somewhere to the right of the other (ignores row). */
    public isRightOf(other: IGridPosition): boolean {
        return this.reel > other.reel;
    }

    /** Returns true if this and other lie on the same diagonal (any distance). */
    public isSameDiagonal(other: IGridPosition): boolean {
        return this.reelDistance(other) === this.rowDistance(other);
    }

    /** Returns true if this position is an immediate orthogonal neighbor (up, down, left, right). */
    public isAdjacent(other: IGridPosition): boolean {
        return this.distanceTo(other, 'manhattan') === 1;
    }

    /** Returns true if this position is an immediate diagonal neighbor. */
    public isDiagonalNeighbour(other: IGridPosition): boolean {
        return this.reelDistance(other) === 1 && this.rowDistance(other) === 1;
    }

    /** Returns true if this position is any immediate neighbor (orthogonal or diagonal). */
    public isNeighbour(other: IGridPosition): boolean {
        return this.distanceTo(other, 'chebyshev') === 1;
    }

    /** Moves this position by the given horizontal and vertical offsets. */
    public shift(horizontalOffset: number, verticalOffset: number): this {
        this.shiftHorizontally(horizontalOffset);
        this.shiftVertically(verticalOffset);

        return this;
    }

    /** Moves this position horizontally by the given offset. */
    public shiftHorizontally(offset: number): this {
        this.reel += offset;

        return this;
    }

    /** Moves this position vertically by the given offset. */
    public shiftVertically(offset: number): this {
        this.row += offset;

        return this;
    }

    /** Moves this position upward by the given distance. */
    public shiftUp(distance = 1): this {
        return this.shiftVertically(-distance);
    }

    /** Moves this position downward by the given distance. */
    public shiftDown(distance = 1): this {
        return this.shiftVertically(distance);
    }

    /** Moves this position leftward by the given distance. */
    public shiftLeft(distance = 1): this {
        return this.shiftHorizontally(-distance);
    }

    /** Moves this position rightward by the given distance. */
    public shiftRight(distance = 1): this {
        return this.shiftHorizontally(distance);
    }

    /**
     * Returns the distance to another position based on the chosen metric:
     * - "manhattan": number of orthogonal steps
     * - "euclidean": straight-line distance
     * - "chebyshev": number of king-like steps (8 directions)
     */
    public distanceTo(other: IGridPosition, distanceType: DistanceType = 'manhattan'): number {
        const dx = this.reelDistance(other);
        const dy = this.rowDistance(other);

        switch (distanceType) {
            case 'euclidean':
                return Math.hypot(dx, dy);
            case 'chebyshev':
                return Math.max(dx, dy);
            case 'manhattan':
            default:
                return dx + dy;
        }
    }

    /**
     * Returns the absolute distance along the horizontal axis to another position.
     */
    public reelDistance(other: IGridPosition): number {
        return Math.abs(this.reel - other.reel);
    }

    /**
     * Returns the absolute distance along the vertical axis to another position.
     */
    public rowDistance(other: IGridPosition): number {
        return Math.abs(this.row - other.row);
    }

    /**
     * Returns true if the given value lies within the provided range
     * under the specified inclusivity.
     */
    private static isValueInRange(value: number, [min, max]: Range, inc: Inclusivity): boolean {
        const lowerBound = inc[0] === '[' ? value >= min : value > min;
        const upperBound = inc[1] === ']' ? value <= max : value < max;

        return lowerBound && upperBound;
    }

    /**
     * Returns true if this position lies within the given range along the specified axis.
     * Defaults to left-inclusive, right-exclusive '[)'.
     */
    protected isInRange(axis: Axis, options: RangeOptions): boolean {
        const {range, inclusivity = '[)'} = options;

        return axis === 'horizontal'
            ? GridPosition.isValueInRange(this.reel, range, inclusivity)
            : GridPosition.isValueInRange(this.row, range, inclusivity);
    }

    /**
     * Returns true if this position's reel (horizontal axis) lies within the given range.
     * Defaults to '[)' inclusivity.
     */
    public isInHorizontalRange(options: RangeOptions): boolean {
        return this.isInRange('horizontal', options);
    }

    /**
     * Returns true if this position's row (vertical axis) lies within the given range.
     * Defaults to '[)' inclusivity.
     */
    public isInVerticalRange(options: RangeOptions): boolean {
        return this.isInRange('vertical', options);
    }

    /**
     * Returns true if this position lies within both horizontal and vertical ranges.
     * Each axis can specify its own inclusivity; both default to '[)'.
     */
    public isWithinBounds(horizontal: RangeOptions, vertical: RangeOptions): boolean {
        return this.isInHorizontalRange(horizontal) && this.isInVerticalRange(vertical);
    }

    /** Copies reel and row from the given position into this instance and returns it. */
    public copyFrom(other: IGridPosition): this {
        this.reel = other.reel;
        this.row = other.row;

        return this;
    }

    /** Writes this instance’s reel and row into the given target and returns that target. */
    public copyTo<T extends IGridPosition>(other: T): T {
        other.reel = this.reel;
        other.row = this.row;

        return other;
    }

    /** Creates and returns a copy of this position. */
    public clone(): GridPosition {
        return new GridPosition(this.reel, this.row);
    }

    /** Returns a string representation of this position. */
    public toString(): string {
        return `(Reel: ${this.reel}; Row: ${this.row})`;
    }
}